import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, merge } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import {
  DialogColors,
  DialogConfigTyped,
  DialogTranslations,
} from '../config';
import { DialogBehaviorService } from './dialog-behavior.service';

interface DialogActionButtonBooleanValues {
  tertiaryButton: boolean;
  additionalButton: boolean;
  cancelButton: boolean;
  confirmButton: boolean;
}

@Injectable()
export class DialogDataService implements OnDestroy {
  get autoClose(): boolean {
    return this.config.autoClose === true;
  }
  get colors(): DialogColors {
    return this.config.colors;
  }
  get translations(): DialogTranslations {
    return this._translations;
  }

  private _translations: DialogTranslations;
  private readonly _inProgress$ =
    new BehaviorSubject<DialogActionButtonBooleanValues>({
      tertiaryButton: false,
      additionalButton: false,
      cancelButton: false,
      confirmButton: false,
    });

  private readonly _disabled$ =
    new BehaviorSubject<DialogActionButtonBooleanValues>({
      tertiaryButton: false,
      additionalButton: false,
      cancelButton: false,
      confirmButton: false,
    });

  private config: DialogConfigTyped<any>;
  private readonly _unsubscribeAll$ = new Subject<void>();
  private _inProgressInstant: DialogActionButtonBooleanValues = {
    tertiaryButton: false,
    additionalButton: false,
    cancelButton: false,
    confirmButton: false,
  };
  private _disabledInstant: DialogActionButtonBooleanValues = {
    tertiaryButton: false,
    additionalButton: false,
    cancelButton: false,
    confirmButton: false,
  };
  private readonly _preActions: Record<
    keyof DialogActionButtonBooleanValues,
    (() => void)[]
  > = {
    tertiaryButton: [],
    additionalButton: [],
    cancelButton: [],
    confirmButton: [],
  };

  constructor(private readonly behaviorDataService: DialogBehaviorService) {}

  setConfig(config: DialogConfigTyped<any>): void {
    this.config = config;

    this._translations = this.config.translations;

    this.setupPreActions();
    this.subscribeToStreams();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll$.next();
    this._unsubscribeAll$.complete();
  }

  getDisabledState(
    key: keyof DialogActionButtonBooleanValues
  ): Observable<boolean> {
    return this._disabled$.pipe(
      map((state) => state[key]),
      distinctUntilChanged()
    );
  }

  getDisabledStateInstant(key: keyof DialogActionButtonBooleanValues): boolean {
    return this._disabledInstant[key] === true;
  }

  getInProgressState(
    key: keyof DialogActionButtonBooleanValues
  ): Observable<boolean> {
    return this._inProgress$.pipe(
      map((state) => state[key]),
      distinctUntilChanged()
    );
  }

  getInProgressStateInstant(
    key: keyof DialogActionButtonBooleanValues
  ): boolean {
    return this._inProgressInstant[key] === true;
  }

  getPreActions(key: keyof DialogActionButtonBooleanValues): (() => void)[] {
    return this._preActions[key];
  }

  disable(enabled: boolean, key: keyof DialogActionButtonBooleanValues): void {
    this._disabledInstant = this._disabled$.value;
    this._disabledInstant[key] = enabled;
    this._disabled$.next(this._disabledInstant);
  }

  inProgress(
    enabled: boolean,
    key: keyof DialogActionButtonBooleanValues
  ): void {
    this._inProgressInstant = this._disabled$.value;
    this._inProgressInstant[key] = enabled;
    this._inProgress$.next(this._inProgressInstant);
  }

  private setupPreActions(): void {
    if (this.config.showInProgressAfterAdditionalAction) {
      this._preActions.additionalButton.push(() =>
        this.inProgress(true, 'additionalButton')
      );
    }

    if (this.config.showInProgressAfterConfirm) {
      this._preActions.confirmButton.push(() =>
        this.inProgress(true, 'confirmButton')
      );
    }
  }

  private subscribeToStreams(): void {
    this.behaviorDataService.onConfirmEnabledChange
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe((enabled) => this.disable(!enabled, 'confirmButton'));

    this.behaviorDataService.onCancelEnabledChange
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe((enabled) => this.disable(!enabled, 'cancelButton'));

    this.behaviorDataService.onAdditionalButtonEnabledChange
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe((enabled) => this.disable(!enabled, 'additionalButton'));

    this.behaviorDataService.onTertiaryButtonEnabledChange
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe((enabled) => this.disable(!enabled, 'tertiaryButton'));

    merge(
      this.behaviorDataService.onConfirmInProgress,
      this.config?.confirmInProgress ?? new Subject<boolean>()
    )
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe((inProgress) => this.inProgress(inProgress, 'confirmButton'));

    merge(
      this.behaviorDataService.onAdditionalButtonInProgress,
      this.config?.additionalButtonInProgress ?? new Subject<boolean>()
    )
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe((inProgress) =>
        this.inProgress(inProgress, 'additionalButton')
      );

    merge(
      this.behaviorDataService.onTertiaryButtonInProgress,
      this.config?.tertiaryButtonInProgress ?? new Subject<boolean>()
    )
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe((inProgress) => this.inProgress(inProgress, 'tertiaryButton'));
  }
}
